import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  FaUsers,
  FaPlus,
  FaChartLine,
  FaSearch,
  FaInbox,
  FaMapMarkerAlt,
  FaUserTie,
  FaStar,
  FaComments,
  FaInfoCircle,
  FaUserPlus,
} from 'react-icons/fa';
import { playerAPI, dashboardAPI, matchChatAPI } from '../services/api';
import { publicAssetUrl } from '../utils/publicAssetUrl';
import { buildPlayerTeamWorkspacePath, normalizePlayerTeamSection } from '../utils/playerTeamWorkspace';
import PlayerDmChatModal from '../components/PlayerDmChatModal';
import OpenPlayersDirectory from '../components/playerDirectory/OpenPlayersDirectory';
import InvitePlayerModal from '../components/playerInvites/InvitePlayerModal';
import TeamDetailExpand from '../components/playerDirectory/TeamDetailExpand';

/** Legacy `view` query param (`?dashboard=1`) maps to DashboardPage embedded section slug. */
const VIEW_TO_SECTION = {
  overview: 'overview',
  statistics: 'stats',
  schedule: 'schedule',
  results: 'results',
  rankings: 'divisions',
  'reserve-court': 'reserve-court',
  'team-members': 'team',
  settings: 'settings',
};

function teamCardActivityTooltip(t, teamName, eng, notif) {
  const n = (Number(eng) || 0) + (Number(notif) || 0);
  if (n <= 0) return '';
  const parts = [];
  if (notif > 0) parts.push(t('phase2.playerDashboard.notifications.tooltipAlerts', { count: notif }));
  if (eng > 0) parts.push(t('phase2.playerDashboard.notifications.tooltipWorkspace', { count: eng }));
  const detail = parts.join(t('phase2.playerDashboard.notifications.tooltipSep'));
  const nm = teamName || t('playerTeamsPage.teamFallback');
  return detail
    ? `${nm}: ${n} (${detail})`
    : t('phase2.playerDashboard.notifications.teamsUnreadTitle', { count: n });
}

function initialsFromName(name) {
  if (!name || typeof name !== 'string') return '?';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return (p[0][0] + p[p.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

const PageWrap = styled.div`
  width: 100%;
  max-width: none;
`;

const Hero = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
  padding: 0.25rem 0 0.5rem;
`;

const HeroText = styled.div`
  flex: 1;
  min-width: min(100%, 280px);
`;

const Kicker = styled.p`
  margin: 0 0 0.35rem 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #64748b;
`;

const PageTitle = styled.h1`
  font-size: clamp(1.65rem, 2.5vw, 2.1rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 0.65rem 0;
  line-height: 1.15;
`;

const HubIntroCard = styled.div`
  margin-top: 0.15rem;
  padding: 1rem 1.2rem 1.05rem;
  border-radius: 14px;
  border: 1px solid rgba(4, 54, 99, 0.14);
  background: linear-gradient(145deg, #f8fafc 0%, #eef2f7 55%, #f1f5f9 100%);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 28px rgba(4, 54, 99, 0.07);
  max-width: 42rem;
`;

const HubIntroHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
`;

const HubIntroIconWrap = styled.div`
  flex-shrink: 0;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 11px;
  background: rgba(4, 54, 99, 0.11);
  color: #043663;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
`;

const HubIntroHeading = styled.p`
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #043663;
  line-height: 1.35;
`;

const HubBulletList = styled.ul`
  margin: 0;
  padding: 0 0 0 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style-type: disc;
`;

const HubBullet = styled.li`
  font-size: 0.9rem;
  line-height: 1.58;
  color: #334155;
  &::marker {
    color: #043663;
    font-weight: 700;
  }
  strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

const PageLead = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: #475569;
  line-height: 1.6;
  max-width: 38rem;
`;

const RegisterBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  flex-shrink: 0;
`;

const RegisterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.7rem 1.15rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  color: #fff;
  background: linear-gradient(135deg, #043663 0%, #0a5a9e 48%, #0f766e 100%);
  box-shadow: 0 10px 28px rgba(4, 54, 99, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
  &:hover {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 14px 32px rgba(4, 54, 99, 0.35);
  }
`;

const RegisterHint = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
`;

const TabRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0 0 1.5rem;
  padding: 0.35rem;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const Tab = styled.button`
  border: none;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#475569')};
  font-weight: 700;
  font-size: 0.8125rem;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${(p) => (p.$active ? '0 4px 14px rgba(4, 54, 99, 0.25)' : 'none')};
  &:hover {
    background: ${(p) => (p.$active ? undefined : 'rgba(241, 245, 249, 0.95)')};
    color: ${(p) => (p.$active ? '#fff' : '#0f172a')};
  }
`;

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.35rem;
`;

/** “My teams” card — reference: clean white tile, navy avatar, triplet league row, recruiting quote, primary CTA + gear */
const TeamCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.35rem 1.35rem 1.25rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  &:hover {
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.09);
    border-color: #cbd5e1;
  }
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const CardTitleCluster = styled.div`
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  min-width: 0;
`;

const TeamAvatarTile = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  flex-shrink: 0;
  background: #043663;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  font-weight: 800;
  color: #7dd3fc;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TeamNameHeading = styled.h3`
  margin: 0;
  font-size: clamp(1.15rem, 2.5vw, 1.35rem);
  font-weight: 800;
  color: #043663;
  letter-spacing: -0.03em;
  line-height: 1.2;
`;

const CardTitleNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const TeamCardUnreadBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 6px;
  border-radius: 999px;
  background: #d52b1e;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

const RoleIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
`;

const CaptainStarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #dc2626;
  color: #fff;
  flex-shrink: 0;
  svg {
    width: 10px;
    height: 10px;
  }
`;

const DiscoveryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  padding: 0.38rem 0.7rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  svg {
    font-size: 0.72rem;
    opacity: 0.9;
  }
  ${(p) =>
    p.$public
      ? `
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  `
      : `
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
  `}
`;

const LeagueTriplet = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  background: #eef2f7;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.85rem;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    text-align: left;
  }
`;

const TripletCell = styled.div`
  text-align: center;
  min-width: 0;
  @media (max-width: 520px) {
    text-align: left;
  }
`;

const TripletLabel = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 0.28rem;
`;

const TripletValue = styled.div`
  font-size: 0.875rem;
  font-weight: 800;
  color: #043663;
  line-height: 1.25;
  word-break: break-word;
`;

const SeasonMetaLine = styled.p`
  margin: -0.35rem 0 0.75rem;
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.45;
  strong {
    color: #334155;
    font-weight: 700;
  }
`;

const StatusHint = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.45;
  color: ${(p) =>
    p.$tone === 'warn' ? '#b45309' : p.$tone === 'muted' ? '#64748b' : '#0369a1'};
`;

const RecruitingBlock = styled.div`
  margin-bottom: 0.85rem;
  padding: 0.65rem 0.85rem 0.65rem 0.95rem;
  border-left: 3px solid #ec4899;
  background: #fafafa;
  border-radius: 0 8px 8px 0;
`;

const RecruitingLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 0.35rem;
`;

const RecruitingQuote = styled.blockquote`
  margin: 0;
  font-size: 0.875rem;
  font-style: italic;
  color: #475569;
  line-height: 1.55;
`;

const RecordRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.32rem 0.58rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  svg {
    opacity: 0.55;
    font-size: 0.68rem;
  }
`;

const CardDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 1rem;
`;

const FooterBtnRow = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: stretch;
`;

const DashboardBtn = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: 10px;
  padding: 0.78rem 0.85rem;
  background: #043663;
  color: #fff;
  font-weight: 800;
  font-size: 0.74rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  &:hover:not(:disabled) {
    background: #062948;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
    transform: none;
  }
`;

const GearBtn = styled.button`
  width: 46px;
  flex-shrink: 0;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  color: #043663;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
  svg {
    font-size: 1.05rem;
  }
`;

const CaptainToolsPanel = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const EmptyState = styled.div`
  background: linear-gradient(180deg, #fff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 20px;
  padding: 2.25rem 1.75rem;
  color: #64748b;
  text-align: center;
  max-width: 480px;
  margin: 0 auto;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.06);
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(4, 54, 99, 0.1), rgba(15, 118, 110, 0.12));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #043663;
  font-size: 1.35rem;
`;

const EmptyTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: center;
  margin-top: 1.25rem;
`;

const EmptyPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.65rem 1.1rem;
  border-radius: 11px;
  font-weight: 700;
  font-size: 0.88rem;
  text-decoration: none;
  color: #fff;
  background: linear-gradient(135deg, #043663, #0f766e);
  box-shadow: 0 8px 20px rgba(4, 54, 99, 0.25);
  &:hover {
    color: #fff;
  }
`;

const EmptySecondary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1rem;
  border-radius: 11px;
  font-weight: 700;
  font-size: 0.88rem;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  align-items: flex-end;
  padding: 1rem 1.15rem;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
`;

const Input = styled.input`
  padding: 0.5rem 0.65rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  min-width: 200px;
`;

const SmallBtn = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => p.$secondary ?? '#043663'};
  color: #fff;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const DangerBtn = styled(SmallBtn)`
  background: #b91c1c;
`;

const SuccessBtn = styled(SmallBtn)`
  background: #0f766e;
`;

const Caption = styled.p`
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0 0 0.75rem;
`;

const DiscoverCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.04), 0 16px 40px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  padding: 0;
  transition: box-shadow 0.2s ease, transform 0.18s ease;
  &:hover {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08), 0 24px 48px rgba(4, 54, 99, 0.1);
    transform: translateY(-2px);
  }
`;

const DiscoverCardInner = styled.div`
  padding: 1.15rem 1.2rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DiscoverTop = styled.div`
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
`;

const DiscoverAvatar = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 16px;
  flex-shrink: 0;
  overflow: hidden;
  border: 2px solid #fff;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.12);
  background: linear-gradient(145deg, #f1f5f9, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.1rem;
  color: #043663;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DiscoverTeamTitle = styled.h3`
  margin: 0 0 0.35rem 0;
  font-size: 1.08rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.025em;
  line-height: 1.25;
`;

const DiscoverMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.45rem 0.85rem;
  margin: 0.35rem 0 0.5rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DiscoverMetaItem = styled.div`
  font-size: 0.8rem;
  line-height: 1.35;
  color: #64748b;
  strong {
    display: block;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    margin-bottom: 0.1rem;
    font-weight: 700;
  }
  span {
    color: #0f172a;
    font-weight: 600;
  }
`;

const DiscoverPitch = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #475569;
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
`;

const OutgoingRequestCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
`;

const IncomingTeamBlock = styled.div`
  margin-bottom: 1.35rem;
  padding: 1rem 1.15rem;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
`;

const IncomingTeamHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
`;

const IncomingTeamAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(145deg, #f1f5f9, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.95rem;
  color: #043663;
  border: 2px solid #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const IncomingTeamTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const ApplicantCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
  border: 1px solid #e2e8f0;
  margin-bottom: 0.55rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;
`;

const JoinPanel = styled.div`
  margin: 0 1.1rem 1.15rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
`;

const DiscoverJoinPanel = styled(JoinPanel)`
  margin: 0.5rem 0 0;
`;

const JoinPanelTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 0.85rem;
  resize: vertical;
`;

const RequestBlock = styled.div`
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 18px;
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
`;

const RequestItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const VALID_TABS = new Set(['my', 'find', 'requests']);

function normalizeTab(tabParam) {
  if (tabParam === 'discover' || tabParam === 'players') return 'find';
  return VALID_TABS.has(tabParam) ? tabParam : 'my';
}

export default function PlayerTeamsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardMode = searchParams.get('dashboard') === '1';
  const dashTenant = searchParams.get('tenant_id') || '';
  const dashCampaign = searchParams.get('campaign_id') || '';
  const dashSeason = searchParams.get('season_id') || '';
  const dashTeam = searchParams.get('team_id') || '';
  const dashViewRaw = searchParams.get('view') || 'overview';
  const showTeamsEmbeddedDashboard =
    dashboardMode && !!dashTenant && !!dashCampaign && !!dashSeason && !!dashTeam;

  const tab = useMemo(() => normalizeTab(searchParams.get('tab')), [searchParams]);
  const setTab = (next) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'my') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true }
    );
  };
  const [teamJoinDmModal, setTeamJoinDmModal] = useState(null);
  const [inviteModalTeam, setInviteModalTeam] = useState(null);

  const { data, isLoading } = useQuery(['playerTeams'], () => playerAPI.getTeams(), { staleTime: 60 * 1000 });
  const teams = Array.isArray(data?.data) ? data.data : data?.data?.data || [];

  const teamIdsKey = useMemo(() => teams.map((row) => row.teamId).filter(Boolean).sort().join(','), [teams]);

  const { data: teamCardActivityById } = useQuery(
    ['playerTeamsCardActivity', teamIdsKey],
    async () => {
      const notifRes = await playerAPI.getNotificationsUnreadSummary();
      const notifByTeam = notifRes?.data?.byTeamId ?? {};
      const out = {};
      await Promise.all(
        teams.map(async (tm) => {
          const tid = String(tm.teamId);
          const notif = Number(notifByTeam[tid]) || 0;
          let eng = 0;
          if (tm.teamId && tm.latestSeasonId) {
            let u = 0;
            try {
              const ur = await matchChatAPI.getUnreadCounts(tm.teamId, tm.tenantId);
              u = Number(ur?.totalUnread ?? 0) || 0;
            } catch (e) {
              /* ignore */
            }
            let p = 0;
            try {
              const extra = tm.tenantId ? { headers: { 'X-Tenant-ID': tm.tenantId } } : {};
              const ov = await dashboardAPI.getOverview(tm.latestSeasonId, tm.teamId, extra);
              const pv = ov?.data?.pendingScoreVerifications || [];
              for (const x of pv) {
                if (x.submittedBy && x.submittedBy !== tm.teamId) p += 1;
              }
            } catch (e) {
              /* ignore */
            }
            eng = u + p;
          }
          out[tid] = { eng, notif, total: eng + notif };
        })
      );
      return out;
    },
    {
      enabled: tab === 'my' && teams.length > 0,
      staleTime: 45 * 1000,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  const { data: myReqRes, isLoading: myReqLoading } = useQuery(
    ['myJoinRequests'],
    () => playerAPI.getMyJoinRequests(),
    { enabled: tab === 'requests', staleTime: 15 * 1000 }
  );
  const myOutgoing = Array.isArray(myReqRes?.data) ? myReqRes.data : [];

  const { data: myInvRes, isLoading: myInvLoading } = useQuery(
    ['myTeamInvitations'],
    () => playerAPI.getMyTeamInvitations(),
    { enabled: tab === 'requests', staleTime: 15 * 1000 }
  );
  const myInvitations = Array.isArray(myInvRes?.data) ? myInvRes.data : [];

  const acceptInvitationMutation = useMutation(
    (invitationId) => playerAPI.acceptTeamInvitation(invitationId),
    {
      onSuccess: () => {
        toast.success(t('playerTeamsPage.toastInviteAccepted'));
        queryClient.invalidateQueries('myTeamInvitations');
        queryClient.invalidateQueries(['playerTeams']);
      },
      onError: (e) =>
        toast.error(e?.response?.data?.message || e.message || t('playerTeamsPage.toastInviteFailed')),
    }
  );

  const declineInvitationMutation = useMutation(
    (invitationId) => playerAPI.declineTeamInvitation(invitationId),
    {
      onSuccess: () => {
        toast.success(t('playerTeamsPage.toastInviteDeclined'));
        queryClient.invalidateQueries('myTeamInvitations');
      },
      onError: (e) =>
        toast.error(e?.response?.data?.message || e.message || t('playerTeamsPage.toastInviteFailed')),
    }
  );

  const captainTeams = useMemo(
    () => teams.filter((row) => row.canManageJoinRequests || row.isCaptain || row.isTeamAdmin),
    [teams]
  );

  const captainTeamIds = useMemo(() => captainTeams.map((row) => row.teamId).join(','), [captainTeams]);

  const { data: incomingBundle, isLoading: incomingLoading } = useQuery(
    ['incomingJoinRequests', captainTeamIds],
    async () => {
      const results = await Promise.all(
        captainTeams.map((team) => playerAPI.getTeamJoinRequests(team.teamId))
      );
      return captainTeams.map((team, i) => ({
        team,
        requests: Array.isArray(results[i]?.data) ? results[i].data : [],
        pendingPartnerInvites: Array.isArray(results[i]?.pendingPartnerInvites)
          ? results[i].pendingPartnerInvites
          : [],
      }));
    },
    { enabled: tab === 'requests' && captainTeams.length > 0, staleTime: 15 * 1000 }
  );

  const openDashboard = (team) => {
    if (!team?.tenantId || !team?.campaignId || !team?.latestSeasonId || !team?.teamId) return;
    navigate(
      buildPlayerTeamWorkspacePath(team.teamId, 'overview', {
        tenant_id: team.tenantId,
        campaign_id: team.campaignId,
        season_id: team.latestSeasonId,
      })
    );
  };

  const cancelJoinMutation = useMutation((teamId) => playerAPI.cancelTeamJoinRequest(teamId), {
    onSuccess: () => {
      toast.success(t('playerTeamsPage.toastCancelled'));
      queryClient.invalidateQueries('myJoinRequests');
      queryClient.invalidateQueries(['playerConversations']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || e.message || t('playerTeamsPage.toastCancelFailed')),
  });

  const joinRequestChatMutation = useMutation(
    ({ teamId, requestId, title: chatTitle }) => playerAPI.ensureJoinRequestConversation(teamId, requestId),
    {
      onSuccess: (res, variables) => {
        const id = res?.data?.conversationId;
        if (id) {
          queryClient.invalidateQueries('myJoinRequests');
          queryClient.invalidateQueries(['incomingJoinRequests']);
          queryClient.invalidateQueries(['playerConversations']);
          setTeamJoinDmModal({
            conversationId: id,
            title: variables.title || t('playerTeamsPage.chat'),
          });
        } else toast.error(t('playerTeamsPage.toastChatOpenFail'));
      },
      onError: (e) => toast.error(e?.response?.data?.message || e.message || t('playerTeamsPage.toastChatFailed')),
    }
  );

  const openJoinRequestChat = (teamId, requestId, conversationId, title) => {
    if (conversationId) {
      setTeamJoinDmModal({
        conversationId,
        title: title || t('playerTeamsPage.chat'),
      });
      return;
    }
    joinRequestChatMutation.mutate({
      teamId,
      requestId,
      title: title || t('playerTeamsPage.chat'),
    });
  };

  const acceptMutation = useMutation(
    ({ teamId, requestId }) => playerAPI.acceptTeamJoinRequest(teamId, requestId),
    {
      onSuccess: () => {
        toast.success(t('playerTeamsPage.toastPlayerAdded'));
        queryClient.invalidateQueries('playerTeams');
        queryClient.invalidateQueries(['incomingJoinRequests']);
        queryClient.invalidateQueries('myJoinRequests');
        queryClient.invalidateQueries(['playerConversations']);
      },
      onError: (e) => {
        const data = e?.response?.data || {};
        const code = data.code;
        if (e?.response?.status === 402 || code === 'payment_required') {
          toast.success(
            data.message ||
              t(
                'playerTeamsPage.toastAcceptNeedsPayment',
                'Accepted. This player still needs to pay League Pass — we sent them a payment invoice.'
              )
          );
          queryClient.invalidateQueries('playerTeams');
          queryClient.invalidateQueries(['incomingJoinRequests']);
          queryClient.invalidateQueries('myJoinRequests');
          return;
        }
        toast.error(data.message || e.message || t('playerTeamsPage.toastAcceptFailed'));
      },
    }
  );

  const cancelPartnerInviteMutation = useMutation(
    ({ teamId, invoiceId }) => playerAPI.cancelPendingPartnerInvoice(teamId, invoiceId),
    {
      onSuccess: (res) => {
        toast.success(
          res?.message ||
            t('playerTeamsPage.toastPartnerInviteCancelled', 'Pending partner invite cancelled.')
        );
        queryClient.invalidateQueries(['incomingJoinRequests']);
      },
      onError: (e) =>
        toast.error(
          e?.response?.data?.message ||
            e.message ||
            t('playerTeamsPage.toastCancelFailed')
        ),
    }
  );

  const declineMutation = useMutation(
    ({ teamId, requestId }) => playerAPI.declineTeamJoinRequest(teamId, requestId),
    {
      onSuccess: () => {
        toast.success(t('playerTeamsPage.toastDeclined'));
        queryClient.invalidateQueries(['incomingJoinRequests']);
        queryClient.invalidateQueries('myJoinRequests');
        queryClient.invalidateQueries(['playerConversations']);
      },
      onError: (e) => toast.error(e?.response?.data?.message || e.message || t('playerTeamsPage.toastDeclineFailed')),
    }
  );

  if (showTeamsEmbeddedDashboard) {
    const rawSection = VIEW_TO_SECTION[dashViewRaw];
    const section = normalizePlayerTeamSection(rawSection);
    return (
      <Navigate
        replace
        to={buildPlayerTeamWorkspacePath(dashTeam, section, {
          tenant_id: dashTenant,
          campaign_id: dashCampaign,
          season_id: dashSeason,
        })}
      />
    );
  }

  return (
    <PageWrap>
      <Hero>
        <HeroText>
          <Kicker>{t('playerTeamsPage.kicker')}</Kicker>
          <PageTitle>{t('playerTeamsPage.title')}</PageTitle>
          <HubIntroCard role="region" aria-label={t('playerTeamsPage.hubIntroAria')}>
            <HubIntroHeader>
              <HubIntroIconWrap aria-hidden>
                <FaInfoCircle />
              </HubIntroIconWrap>
              <HubIntroHeading>{t('playerTeamsPage.hubIntroTitle')}</HubIntroHeading>
            </HubIntroHeader>
            <HubBulletList>
              <HubBullet>
                <Trans i18nKey="playerTeamsPage.hubBullet1" components={{ strong: <strong /> }} />
              </HubBullet>
              <HubBullet>
                <Trans i18nKey="playerTeamsPage.hubBullet2" components={{ strong: <strong /> }} />
              </HubBullet>
              <HubBullet>
                <Trans i18nKey="playerTeamsPage.hubBullet3" components={{ strong: <strong /> }} />
              </HubBullet>
            </HubBulletList>
          </HubIntroCard>
        </HeroText>
        <RegisterBlock>
          <RegisterLink to="/player/tournaments">
            <FaPlus aria-hidden /> {t('playerTeamsPage.registerNewTeam')}
          </RegisterLink>
          <RegisterHint>{t('playerTeamsPage.registerHint')}</RegisterHint>
        </RegisterBlock>
      </Hero>

      <TabRow role="tablist" aria-label={t('playerTeamsPage.tabAria')}>
        <Tab type="button" role="tab" $active={tab === 'my'} onClick={() => setTab('my')} aria-selected={tab === 'my'}>
          <FaUsers aria-hidden /> {t('playerTeamsPage.tabMyTeams')}
        </Tab>
        <Tab type="button" role="tab" $active={tab === 'find'} onClick={() => setTab('find')} aria-selected={tab === 'find'}>
          <FaSearch aria-hidden /> {t('playerTeamsPage.tabDiscover')}
        </Tab>
        <Tab type="button" role="tab" $active={tab === 'requests'} onClick={() => setTab('requests')} aria-selected={tab === 'requests'}>
          <FaInbox aria-hidden /> {t('playerTeamsPage.tabRequests')}
        </Tab>
      </TabRow>

      {tab === 'my' && (
        <>
          {isLoading ? (
            <EmptyState style={{ padding: '2rem' }}>{t('playerTeamsPage.loadingTeams')}</EmptyState>
          ) : teams.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <FaUsers aria-hidden />
              </EmptyIcon>
              <EmptyTitle>{t('playerTeamsPage.emptyTitle')}</EmptyTitle>
              <PageLead style={{ margin: '0 auto', textAlign: 'center' }}>
                {t('playerTeamsPage.emptyLead')}
              </PageLead>
              <EmptyActions>
                <EmptyPrimary to="/player/tournaments">
                  <FaPlus aria-hidden /> {t('playerTeamsPage.registerTeam')}
                </EmptyPrimary>
                <EmptySecondary type="button" onClick={() => setTab('find')}>
                  <FaSearch aria-hidden /> {t('playerTeamsPage.browseDiscover')}
                </EmptySecondary>
              </EmptyActions>
            </EmptyState>
          ) : (
            <TeamsGrid>
              {teams.map((team) => {
                const hasContext =
                  !!team.tenantId && !!team.campaignId && !!team.latestSeasonId && !!team.teamId;
                const isCaptain = team.isCaptain === true || String(team.role) === 'Captain';
                const logoSrc = team.teamLogoUrl ? publicAssetUrl(team.teamLogoUrl) : '';
                const initials =
                  (team.teamName || 'T')
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase() || 'T';

                const regLooksInactive =
                  team.latestSeasonId &&
                  (team.seasonRegistrationIsActive === false ||
                    team.seasonRegistrationIsActive === 'f' ||
                    String(team.seasonRegistrationIsActive).toLowerCase() === 'false');
                const legacyDisabled =
                  team.teamIsActive === false || regLooksInactive;

                const canOpenDashboard =
                  typeof team.canOpenLeagueDashboard === 'boolean'
                    ? team.canOpenLeagueDashboard
                    : hasContext && !legacyDisabled;

                let statusVariant = 'neutral';
                let statusText = t('playerTeamsPage.statusDefault');
                if (team.teamCardStatus) {
                  statusVariant = team.teamCardStatus;
                  statusText = team.teamCardStatusMessage || statusText;
                } else if (legacyDisabled) {
                  statusVariant = 'disabled';
                  statusText =
                    team.teamIsActive === false
                      ? t('playerTeamsPage.statusDisabledAdmin')
                      : t('playerTeamsPage.statusDisabledSeason');
                } else {
                  if (team.latestSeasonActiveNow) {
                    statusVariant = 'active';
                    statusText = t('playerTeamsPage.statusActive');
                  } else if (team.latestSeasonRegistrationOpen) {
                    statusVariant = 'reg';
                    statusText = t('playerTeamsPage.statusRegOpen');
                  } else if (team.latestSeasonStatus) {
                    statusText = t('playerTeamsPage.seasonStatus', {
                      status: String(team.latestSeasonStatus).replace(/_/g, ' '),
                    });
                  }
                }
                const stripVariant = ['active', 'reg', 'neutral', 'disabled', 'completed'].includes(statusVariant)
                  ? statusVariant
                  : 'neutral';
                const showJoinGear = !!team.canManageJoinRequests;
                return (
                  <TeamCard key={team.teamId}>
                    <CardHeaderRow>
                      <CardTitleCluster>
                        <TeamAvatarTile>
                          {logoSrc ? (
                            <img src={logoSrc} alt="" loading="lazy" />
                          ) : (
                            <span aria-hidden>{initials.slice(0, 3)}</span>
                          )}
                        </TeamAvatarTile>
                        <div style={{ minWidth: 0 }}>
                          <CardTitleNameRow>
                            <TeamNameHeading>{team.teamName || t('playerTeamsPage.teamFallback')}</TeamNameHeading>
                            {(() => {
                              const tid = String(team.teamId);
                              const act = teamCardActivityById?.[tid];
                              const total = act?.total ?? 0;
                              if (!total) return null;
                              const tip = teamCardActivityTooltip(
                                t,
                                team.teamName || t('playerTeamsPage.teamFallback'),
                                act.eng,
                                act.notif
                              );
                              const label = total > 99 ? '99+' : String(total);
                              return (
                                <TeamCardUnreadBadge title={tip} aria-label={tip}>
                                  {label}
                                </TeamCardUnreadBadge>
                              );
                            })()}
                          </CardTitleNameRow>
                          <RoleIndicator>
                            {isCaptain ? (
                              <>
                                <CaptainStarBadge aria-hidden>
                                  <FaStar />
                                </CaptainStarBadge>
                                {t('playerTeamsPage.captainRole')}
                              </>
                            ) : (
                              <span>{team.role || t('playerTeamsPage.memberRole')}</span>
                            )}
                          </RoleIndicator>
                        </div>
                      </CardTitleCluster>
                    </CardHeaderRow>

                    <LeagueTriplet>
                      <TripletCell>
                        <TripletLabel>{t('playerTeamsPage.tripletLeague')}</TripletLabel>
                        <TripletValue>{team.tenantName || '—'}</TripletValue>
                      </TripletCell>
                      <TripletCell>
                        <TripletLabel>{t('playerTeamsPage.tripletCampaign')}</TripletLabel>
                        <TripletValue>{team.campaignTitle || '—'}</TripletValue>
                      </TripletCell>
                      <TripletCell>
                        <TripletLabel>{t('playerTeamsPage.tripletDivision')}</TripletLabel>
                        <TripletValue>{team.divisionName || '—'}</TripletValue>
                      </TripletCell>
                    </LeagueTriplet>

                    {(team.latestSeasonName || team.teamCanton || team.captainDisplayName) && (
                      <SeasonMetaLine>
                        {team.latestSeasonName ? (
                          <>
                            <strong>{t('playerTeamsPage.seasonLabel')}</strong> {team.latestSeasonName}
                          </>
                        ) : null}
                        {team.latestSeasonName && (team.teamCanton || team.captainDisplayName)
                          ? ' · '
                          : null}
                        {team.teamCanton ? (
                          <>
                            <strong>{t('playerTeamsPage.cantonLabel')}</strong> {team.teamCanton}
                          </>
                        ) : null}
                        {team.captainDisplayName ? (
                          <>
                            {team.latestSeasonName || team.teamCanton ? ' · ' : ''}
                            <strong>{t('playerTeamsPage.captainLabel')}</strong> {team.captainDisplayName}
                          </>
                        ) : null}
                      </SeasonMetaLine>
                    )}

                    {(legacyDisabled ||
                      stripVariant === 'completed' ||
                      stripVariant === 'disabled') && (
                      <StatusHint $tone="warn">{statusText}</StatusHint>
                    )}
                    {stripVariant === 'reg' && !legacyDisabled ? (
                      <StatusHint $tone="info">{statusText}</StatusHint>
                    ) : null}
                    {stripVariant === 'active' && !legacyDisabled ? (
                      <StatusHint $tone="muted">{statusText}</StatusHint>
                    ) : null}

                    <RecordRow>
                      <StatChip>
                        <FaChartLine aria-hidden /> {team.wins || 0}W
                      </StatChip>
                      <StatChip>{team.losses || 0}L</StatChip>
                      <StatChip>{team.completedMatches || 0} {t('playerTeamsPage.playedSuffix')}</StatChip>
                    </RecordRow>

                    <CardDivider />

                    <FooterBtnRow>
                      {team.pendingRegistrationCtaPath ? (
                        <DashboardBtn
                          type="button"
                          onClick={() => navigate(team.pendingRegistrationCtaPath)}
                        >
                          {t('playerTeamsPage.payRegistrationHalf', 'Pay registration')}
                        </DashboardBtn>
                      ) : (
                        <DashboardBtn
                          type="button"
                          onClick={() => openDashboard(team)}
                          disabled={!canOpenDashboard}
                          title={
                            !canOpenDashboard && team.teamIsActive === false
                              ? t('playerTeamsPage.dashboardDisabledDeactivated')
                              : !canOpenDashboard && team.seasonRegistrationIsActive === false
                                ? t('playerTeamsPage.dashboardDisabledSeason')
                                : undefined
                          }
                        >
                          {t('playerTeamsPage.openDashboard')}
                        </DashboardBtn>
                      )}
                      {showJoinGear && (team.activeMemberCount == null || Number(team.activeMemberCount) < 2) ? (
                        <SmallBtn
                          type="button"
                          onClick={() =>
                            setInviteModalTeam({ teamId: team.teamId, teamName: team.teamName })
                          }
                        >
                          <FaUserPlus size={12} style={{ marginRight: 6 }} aria-hidden />
                          {t('playerTeamsPage.invitePlayer')}
                        </SmallBtn>
                      ) : null}
                    </FooterBtnRow>
                  </TeamCard>
                );
              })}
            </TeamsGrid>
          )}
        </>
      )}

      {tab === 'find' && <OpenPlayersDirectory />}

      {tab === 'requests' && (
        <>
          <RequestBlock>
            <JoinPanelTitle>{t('playerTeamsPage.teamInvitationsTitle')}</JoinPanelTitle>
            <Caption style={{ color: '#64748b', marginTop: '0.25rem', marginBottom: '0.75rem' }}>
              {t('playerTeamsPage.teamInvitationsDescription')}
            </Caption>
            {myInvLoading ? (
              <EmptyState>{t('playerTeamsPage.loadingShort')}</EmptyState>
            ) : myInvitations.length === 0 ? (
              <Caption style={{ color: '#64748b' }}>{t('playerTeamsPage.noTeamInvitations')}</Caption>
            ) : (
              myInvitations.map((inv) => {
                const inviterName =
                  inv.inviterDisplayName ||
                  [inv.inviterFirstName, inv.inviterLastName].filter(Boolean).join(' ') ||
                  '—';
                return (
                  <OutgoingRequestCard key={inv.id}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.92rem', color: '#334155', lineHeight: 1.45 }}>
                        <Trans
                          i18nKey="playerTeamsPage.invitationLead"
                          values={{ inviter: inviterName }}
                          components={{ strong: <strong /> }}
                        />
                      </div>
                      <div style={{ marginTop: '0.35rem' }}>
                        <TeamDetailExpand
                          teamName={inv.teamName}
                          teamCanton={inv.teamCanton}
                          divisionName={inv.divisionName}
                          teamSkillLevel={inv.teamSkillLevel}
                          teamLogoUrl={inv.teamLogoUrl}
                        />
                      </div>
                      {inv.message ? (
                        <DiscoverPitch style={{ fontSize: '0.82rem', marginTop: '0.45rem' }}>
                          {inv.message}
                        </DiscoverPitch>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flexShrink: 0 }}>
                      {inv.requiresPayment && inv.invoiceId ? (
                        <SuccessBtn
                          type="button"
                          onClick={() => navigate(`/player/registration-invoice/${inv.invoiceId}`)}
                        >
                          {t('playerTeamsPage.completeInvoice', 'Complete invoice')}
                        </SuccessBtn>
                      ) : (
                        <SuccessBtn
                          type="button"
                          onClick={() => acceptInvitationMutation.mutate(inv.id)}
                          disabled={acceptInvitationMutation.isLoading}
                        >
                          {t('playerTeamsPage.accept')}
                        </SuccessBtn>
                      )}
                      <DangerBtn
                        type="button"
                        onClick={() =>
                          inv.requiresPayment
                            ? playerAPI
                                .rejectRegistrationInvite(inv.id)
                                .then(() => {
                                  toast.success(t('playerTeamsPage.toastInviteDeclined'));
                                  queryClient.invalidateQueries('myTeamInvitations');
                                })
                                .catch((e) =>
                                  toast.error(e?.response?.data?.message || e.message)
                                )
                            : declineInvitationMutation.mutate(inv.id)
                        }
                        disabled={declineInvitationMutation.isLoading}
                      >
                        {t('playerTeamsPage.decline')}
                      </DangerBtn>
                    </div>
                  </OutgoingRequestCard>
                );
              })
            )}
          </RequestBlock>

          <RequestBlock>
            <JoinPanelTitle>{t('playerTeamsPage.outgoingTitle')}</JoinPanelTitle>
            <Caption style={{ color: '#64748b', marginTop: '0.25rem', marginBottom: '0.75rem' }}>
              {t('playerTeamsPage.outgoingDescription')}
            </Caption>
            {myReqLoading ? (
              <EmptyState>{t('playerTeamsPage.loadingShort')}</EmptyState>
            ) : myOutgoing.length === 0 ? (
              <Caption style={{ color: '#64748b' }}>{t('playerTeamsPage.noOutgoing')}</Caption>
            ) : (
              myOutgoing.map((r) => {
                const oLogo = r.team_logo_url ? publicAssetUrl(r.team_logo_url) : '';
                const oInit = initialsFromName(r.team_name);
                return (
                  <OutgoingRequestCard key={r.id}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                      <DiscoverAvatar style={{ width: 52, height: 52, borderRadius: 14 }}>
                        {oLogo ? (
                          <img src={oLogo} alt="" loading="lazy" />
                        ) : (
                          <span aria-hidden>{oInit.slice(0, 2)}</span>
                        )}
                      </DiscoverAvatar>
                      <div>
                        <strong style={{ fontSize: '1rem', color: '#0f172a' }}>{r.team_name}</strong>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
                          <FaMapMarkerAlt size={11} style={{ marginRight: 4, opacity: 0.75 }} aria-hidden />
                          {r.canton || '—'}
                          {r.division_name ? ` · ${r.division_name}` : ''}
                        </div>
                        {r.captain_name ? (
                          <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.2rem' }}>
                            <FaUserTie size={11} style={{ marginRight: 4, opacity: 0.75 }} aria-hidden />
                            {t('playerTeamsPage.captainPrefix')} {r.captain_name}
                          </div>
                        ) : null}
                        {r.team_skill_level != null ? (
                          <div style={{ fontSize: '0.78rem', color: '#0f766e', fontWeight: 700, marginTop: '0.2rem' }}>
                            {t('playerTeamsPage.skillPrefix')} {r.team_skill_level}
                          </div>
                        ) : null}
                        {r.message ? (
                          <DiscoverPitch style={{ fontSize: '0.82rem', marginTop: '0.45rem' }}>
                            {r.message}
                          </DiscoverPitch>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
                      <SmallBtn
                        type="button"
                        onClick={() => openJoinRequestChat(r.team_id, r.id, r.conversation_id, r.team_name)}
                        disabled={joinRequestChatMutation.isLoading}
                        $secondary="#475569"
                      >
                        <FaComments size={13} style={{ marginRight: 6 }} aria-hidden />
                        {t('playerTeamsPage.chat')}
                      </SmallBtn>
                      <DangerBtn
                        type="button"
                        onClick={() => cancelJoinMutation.mutate(r.team_id)}
                        disabled={cancelJoinMutation.isLoading}
                      >
                        {t('playerTeamsPage.cancel')}
                      </DangerBtn>
                    </div>
                  </OutgoingRequestCard>
                );
              })
            )}
          </RequestBlock>

          <RequestBlock>
            <JoinPanelTitle>{t('playerTeamsPage.incomingTitle')}</JoinPanelTitle>
            <Caption style={{ color: '#64748b', marginTop: '0.25rem', marginBottom: '0.75rem' }}>
              {t('playerTeamsPage.incomingDescription')}
            </Caption>
            {captainTeams.length === 0 ? (
              <Caption style={{ color: '#64748b' }}>
                {t('playerTeamsPage.incomingNotCaptain')}
              </Caption>
            ) : incomingLoading ? (
              <Caption>{t('playerTeamsPage.loadingShort')}</Caption>
            ) : (
              (incomingBundle || []).map(({ team, requests: list, pendingPartnerInvites }) => {
                const thLogo = team.teamLogoUrl ? publicAssetUrl(team.teamLogoUrl) : '';
                const thInit = initialsFromName(team.teamName);
                return (
                  <IncomingTeamBlock key={team.teamId}>
                    <IncomingTeamHeader>
                      <IncomingTeamAvatar>
                        {thLogo ? (
                          <img src={thLogo} alt="" loading="lazy" />
                        ) : (
                          <span aria-hidden>{thInit.slice(0, 2)}</span>
                        )}
                      </IncomingTeamAvatar>
                      <div>
                        <IncomingTeamTitle>{team.teamName}</IncomingTeamTitle>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.2rem' }}>
                          {team.teamCanton || '—'}
                          {team.divisionName ? ` · ${team.divisionName}` : ''}
                        </div>
                      </div>
                    </IncomingTeamHeader>
                    {(pendingPartnerInvites || []).length > 0 ? (
                      <div style={{ marginBottom: list.length ? '0.85rem' : 0 }}>
                        <Caption style={{ color: '#92400e', marginBottom: '0.5rem' }}>
                          {t(
                            'playerTeamsPage.pendingPartnerDescription',
                            'A partner payment invite is already waiting. Cancel it before you can accept another player.'
                          )}
                        </Caption>
                        {(pendingPartnerInvites || []).map((inv) => {
                          const pendingName = [inv.first_name, inv.last_name]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          const pendingEmail = inv.email || inv.invite_email || '—';
                          return (
                            <ApplicantCard key={inv.invoice_id}>
                              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                                <div style={{ fontSize: '0.92rem', color: '#334155', lineHeight: 1.45 }}>
                                  {t(
                                    'playerTeamsPage.pendingPartnerWaiting',
                                    'Waiting for partner to pay'
                                  )}
                                  {pendingName ? `: ${pendingName}` : ''}
                                </div>
                                <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.2rem' }}>
                                  {pendingEmail}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flexShrink: 0 }}>
                                <DangerBtn
                                  type="button"
                                  onClick={() =>
                                    cancelPartnerInviteMutation.mutate({
                                      teamId: team.teamId,
                                      invoiceId: inv.invoice_id,
                                    })
                                  }
                                  disabled={cancelPartnerInviteMutation.isLoading}
                                >
                                  {t('playerTeamsPage.cancelPartnerInvite', 'Cancel invite')}
                                </DangerBtn>
                              </div>
                            </ApplicantCard>
                          );
                        })}
                      </div>
                    ) : null}
                    {list.length === 0 && !(pendingPartnerInvites || []).length ? (
                      <Caption>{t('playerTeamsPage.noIncomingPending')}</Caption>
                    ) : (
                      list.map((req) => {
                        const applicantName =
                          [req.first_name, req.last_name].filter(Boolean).join(' ').trim() || '—';
                        return (
                        <ApplicantCard key={req.id}>
                          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                            <div style={{ fontSize: '0.92rem', color: '#334155', lineHeight: 1.45 }}>
                              <Trans
                                i18nKey="playerTeamsPage.incomingJoinLead"
                                values={{ name: applicantName, team: team.teamName }}
                                components={[<strong />, <strong />]}
                              />
                            </div>
                            <div style={{ marginTop: '0.35rem' }}>
                              <TeamDetailExpand
                                teamName={team.teamName}
                                teamCanton={team.teamCanton}
                                divisionName={team.divisionName}
                                teamSkillLevel={team.teamSkillLevel}
                                teamLogoUrl={team.teamLogoUrl}
                              />
                            </div>
                            <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.2rem' }}>{req.email}</div>
                            {req.home_canton ? (
                              <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.3rem' }}>
                                <FaMapMarkerAlt size={11} style={{ marginRight: 4, opacity: 0.75 }} aria-hidden />
                                <strong style={{ fontWeight: 700, color: '#475569' }}>
                                  {t('playerTeamsPage.registeredCanton')}:
                                </strong>{' '}
                                {req.home_canton}
                              </div>
                            ) : null}
                            {req.skill_level != null && (
                              <div
                                style={{
                                  fontSize: '0.78rem',
                                  color: '#0f766e',
                                  fontWeight: 700,
                                  marginTop: '0.35rem',
                                }}
                              >
                                {t('playerTeamsPage.declaredSkill', { level: req.skill_level })}
                              </div>
                            )}
                            {req.message ? (
                              <div style={{ marginTop: '0.5rem' }}>
                                <div
                                  style={{
                                    fontSize: '0.65rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    color: '#94a3b8',
                                    fontWeight: 700,
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  {t('playerTeamsPage.applicantMessageLabel')}
                                </div>
                                <DiscoverPitch style={{ fontSize: '0.82rem', padding: '0.55rem 0.65rem' }}>
                                  {req.message}
                                </DiscoverPitch>
                              </div>
                            ) : null}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
                            <SmallBtn
                              type="button"
                              onClick={() =>
                                openJoinRequestChat(
                                  team.teamId,
                                  req.id,
                                  req.conversation_id,
                                  [req.first_name, req.last_name].filter(Boolean).join(' ').trim()
                                )
                              }
                              disabled={joinRequestChatMutation.isLoading}
                              $secondary="#475569"
                            >
                              <FaComments size={13} style={{ marginRight: 6 }} aria-hidden />
                              {t('playerTeamsPage.chat')}
                            </SmallBtn>
                            <SuccessBtn
                              type="button"
                              onClick={() =>
                                acceptMutation.mutate({ teamId: team.teamId, requestId: req.id })
                              }
                              disabled={acceptMutation.isLoading}
                            >
                              {t('playerTeamsPage.accept')}
                            </SuccessBtn>
                            <DangerBtn
                              type="button"
                              onClick={() =>
                                declineMutation.mutate({ teamId: team.teamId, requestId: req.id })
                              }
                              disabled={declineMutation.isLoading}
                            >
                              {t('playerTeamsPage.decline')}
                            </DangerBtn>
                          </div>
                        </ApplicantCard>
                        );
                      })
                    )}
                  </IncomingTeamBlock>
                );
              })
            )}
          </RequestBlock>
        </>
      )}
      <PlayerDmChatModal
        isOpen={!!teamJoinDmModal?.conversationId}
        conversationId={teamJoinDmModal?.conversationId || ''}
        title={teamJoinDmModal?.title || ''}
        subtitle={t('playerTeamsPage.joinRequestChatModalSubtitle')}
        onClose={() => setTeamJoinDmModal(null)}
      />
      {inviteModalTeam ? (
        <InvitePlayerModal
          teamId={inviteModalTeam.teamId}
          teamName={inviteModalTeam.teamName}
          onClose={() => setInviteModalTeam(null)}
          onSuccess={() => queryClient.invalidateQueries(['playerTeams'])}
        />
      ) : null}
    </PageWrap>
  );
}
